import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { BarChart2 } from 'lucide-react';
import { useSelectedMetrics, isMetricSelected } from '../utils/metricsUtils';

/**
 * Composant pour afficher le score de qualité détaillé par mot-clé
 * Reprend exactement le même design que le composant Mots-clés
 */
const DetailedQualityScoreTable = ({ 
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  selectedCampaignFilter,
  selectedQualityScoreFilter,
  selectedMatchTypeFilter,
  selectedCpcTierFilter,
  token
}) => {
  const [keywordsData, setKeywordsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'qualityScore', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Hook pour les métriques sélectionnées (pour la cohérence, même si ce tableau se concentre sur la qualité)
  const selectedMetrics = useSelectedMetrics();

  // Fonction pour charger les données des mots-clés
  const loadKeywords = async () => {
    if (!selectedAccount || !token) return;
    
    try {
      setLoading(true);
      setError(null);
      const requestBody = {
        sources: [selectedAccount.id],
        period: selectedPeriod
      };

      if (selectedPeriod === 'CUSTOM') {
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      const response = await axios.post(
        `${config.API_URL}/api/dashboard/keywords`,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const data = response.data.data[0];
        setKeywordsData(data);
      } else {
        setKeywordsData(null);
      }
    } catch (err) {
      console.error('❌ [DETAILED-QUALITY-SCORE] Erreur:', err);
      setError('Erreur lors du chargement des scores de qualité');
      setKeywordsData(null);
    } finally {
      setLoading(false);
    }
  };

  // Charger les données quand les dépendances changent
  useEffect(() => {
    if (selectedAccount && selectedPeriod && isVisible) {
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        return;
      }
      loadKeywords();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, isVisible]);

  // Fonction de tri
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  // Fonction pour obtenir la couleur et le texte d'une qualité
  const getQualityDisplay = (qualityValue) => {
    if (!qualityValue) return { text: '-', className: 'quality-unknown' };
    
    const value = qualityValue.toLowerCase();
    
    // Valeurs françaises du backend
    if (value === 'excellent' || value === 'supérieur à la moyenne') {
      return { text: 'Excellent', className: 'quality-excellent' };
    }
    if (value === 'bon' || value === 'moyenne') {
      return { text: 'Bon', className: 'quality-good' };
    }
    if (value === 'mauvais' || value === 'inférieur à la moyenne') {
      return { text: 'Mauvais', className: 'quality-bad' };
    }
    
    // Valeurs anglaises (fallback)
    if (value === 'above_average') {
      return { text: 'Excellent', className: 'quality-excellent' };
    }
    if (value === 'average') {
      return { text: 'Bon', className: 'quality-good' };
    }
    if (value === 'below_average') {
      return { text: 'Mauvais', className: 'quality-bad' };
    }
    
    // Cas spéciaux
    if (value.includes('valeur')) {
      return { text: qualityValue, className: 'quality-unknown' };
    }
    
    return { text: qualityValue, className: 'quality-unknown' };
  };

  // Trier les données
  const sortedKeywords = React.useMemo(() => {
    if (!keywordsData?.keywords) return [];
    
    // Filtrer les mots-clés qui ont au moins un score de qualité
    const keywordsWithQuality = keywordsData.keywords.filter(keyword => 
      keyword.qualityScore !== null && 
      keyword.qualityScore !== undefined &&
      (keyword.creativeQualityScore || keyword.postClickQualityScore || keyword.searchPredictedCtr)
    );
    
    return [...keywordsWithQuality].sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.key === 'qualityScore') {
        aValue = a.qualityScore ?? 0;
        bValue = b.qualityScore ?? 0;
      } else if (sortConfig.key === 'keyword' || sortConfig.key === 'campaignName' || sortConfig.key === 'adGroupName') {
        aValue = a[sortConfig.key]?.toLowerCase() || '';
        bValue = b[sortConfig.key]?.toLowerCase() || '';
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        // Pour les autres colonnes qualité, on trie par ordre alphabétique de la valeur
        aValue = a[sortConfig.key]?.toLowerCase() || 'zzz';
        bValue = b[sortConfig.key]?.toLowerCase() || 'zzz';
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [keywordsData, sortConfig]);

  // Filtrer par campagne et score de qualité si nécessaire
  const filteredKeywords = React.useMemo(() => {
    let filtered = sortedKeywords;
    
    // Filtre par campagne
    if (selectedCampaignFilter) {
      filtered = filtered.filter(keyword => 
        keyword.campaignId === selectedCampaignFilter.id ||
        keyword.campaignName === selectedCampaignFilter.name
      );
    }
    
    // Filtre par score de qualité
    if (selectedQualityScoreFilter !== null && selectedQualityScoreFilter !== undefined) {
      filtered = filtered.filter(keyword => {
        const keywordScore = keyword.qualityScore || 0;
        return keywordScore === selectedQualityScoreFilter;
      });
    }
    
    // Filtre par type de correspondance
    if (selectedMatchTypeFilter !== null && selectedMatchTypeFilter !== undefined) {
      filtered = filtered.filter(keyword => {
        return keyword.matchType === selectedMatchTypeFilter;
      });
    }
    
    // Filtre par palier CPC
    if (selectedCpcTierFilter !== null && selectedCpcTierFilter !== undefined) {
      filtered = filtered.filter(keyword => {
        const cpc = Number(keyword.cpc) || 0;
        return cpc >= selectedCpcTierFilter.min && cpc <= selectedCpcTierFilter.max;
      });
    }
    
    return filtered;
  }, [sortedKeywords, selectedCampaignFilter, selectedQualityScoreFilter, selectedMatchTypeFilter, selectedCpcTierFilter]);

  // Pagination
  const paginatedKeywords = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredKeywords.slice(startIndex, endIndex);
  }, [filteredKeywords, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredKeywords.length / itemsPerPage);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><BarChart2 size={16} />&nbsp;Score de Qualité détaillé par Mot-clé</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {selectedCampaignFilter && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px', 
              padding: '4px 8px', 
              background: '#e3f2fd', 
              borderRadius: '4px',
              fontSize: '0.9em'
            }}>
              <span>Campagne: {selectedCampaignFilter.name}</span>
              <button 
                onClick={() => window.setSelectedCampaignFilter && window.setSelectedCampaignFilter(null)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer',
                  padding: '2px',
                  color: '#666'
                }}
                title="Supprimer le filtre campagne"
              >
                ✕
              </button>
            </div>
          )}
          <button 
            onClick={onToggleVisibility}
            className="section-toggle-btn"
            title="Masquer/Afficher la section"
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>
      </div>

      {!isVisible ? null : (
        <>
          {loading ? (
            <div className="loading-state" style={{ flexDirection: 'column' }}>
              <div className="loading-spinner"></div>
              Chargement des scores de qualité...
            </div>
          ) : error ? (
            <div className="error-message">
              {error}
            </div>
          ) : !keywordsData || !keywordsData.keywords || filteredKeywords.length === 0 ? (
            <div className="empty-message">
              Aucun mot-clé avec score de qualité trouvé pour la période sélectionnée
            </div>
          ) : (
            <>
              <div className="campaigns-table-wrapper">
                <table 
                  key={selectedMetrics.filter(m => m.selected).map(m => m.key).join(',')}
                  className="campaigns-table"
                  style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}
                >
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('keyword')} className="sortable">
                        Mot-clé {sortConfig.key === 'keyword' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('campaignName')} className="sortable">
                        Campagne {sortConfig.key === 'campaignName' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('adGroupName')} className="sortable">
                        Groupe d'annonce {sortConfig.key === 'adGroupName' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('qualityScore')} className="sortable numeric">
                        Score qualité {sortConfig.key === 'qualityScore' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('creativeQualityScore')} className="sortable">
                        Qualité de l'annonce {sortConfig.key === 'creativeQualityScore' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('postClickQualityScore')} className="sortable">
                        Expérience page de destination {sortConfig.key === 'postClickQualityScore' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('searchPredictedCtr')} className="sortable">
                        Taux de clic attendu {sortConfig.key === 'searchPredictedCtr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedKeywords.map((keyword, index) => {
                      const adQualityDisplay = getQualityDisplay(keyword.creativeQualityScore);
                      const landingPageDisplay = getQualityDisplay(keyword.postClickQualityScore);
                      const expectedCtrDisplay = getQualityDisplay(keyword.searchPredictedCtr);
                      
                      return (
                        <tr key={`${keyword.id}-${index}`}>
                          <td className="keyword-name">
                            {keyword.keyword}
                          </td>
                          <td>
                            {keyword.campaignName}
                          </td>
                          <td>
                            {keyword.adGroupName}
                          </td>
                          <td className="numeric" style={{ textAlign: 'center' }}>
                            <span className={`quality-score quality-score-${keyword.qualityScore || 0}`}>
                              {keyword.qualityScore && keyword.qualityScore >= 1 && keyword.qualityScore <= 10 ? keyword.qualityScore : '-'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`quality-cell ${adQualityDisplay.className}`}>
                              {adQualityDisplay.text}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`quality-cell ${landingPageDisplay.className}`}>
                              {landingPageDisplay.text}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`quality-cell ${expectedCtrDisplay.className}`}>
                              {expectedCtrDisplay.text}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="totals-row">
                      <td colSpan="7"><strong>TOTAL ({filteredKeywords.length} mots-clés avec score de qualité)</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Affichage {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredKeywords.length)} sur {filteredKeywords.length} mots-clés
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      ← Précédent
                    </button>
                    <span className="page-numbers">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DetailedQualityScoreTable;